import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { DEFAULT_SITE_CONFIG } from "@/data/siteConfig";
import { fetchCollections } from "@/lib/shopify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGACY_PATHS = { dresses: "/dresses", "new-arrivals": "/new-arrivals", sale: "/sale" };
const linkForHandle = (h) => LEGACY_PATHS[h] || `/collection/${h}`;

const FALLBACK_SHOP = [
  { to: "/dresses", label: "Dresses" },
  { to: "/new-arrivals", label: "New Arrivals" },
  { to: "/sale", label: "Sale" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(DEFAULT_SITE_CONFIG.footer);
  const [shopLinks, setShopLinks] = useState(FALLBACK_SHOP);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/site-config`).then(({ data }) => {
      if (alive && data?.footer) setCfg({ ...DEFAULT_SITE_CONFIG.footer, ...data.footer });
    }).catch(() => {});
    fetchCollections().then((cols) => {
      if (!alive || !cols?.length) return;
      setShopLinks(cols.map((c) => ({ to: linkForHandle(c.handle), label: c.title })));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Welcome to Vastra West.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative mt-24 md:mt-32"
      style={{ background: "var(--vw-ink)", color: "var(--vw-bg)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Newsletter */}
        <div className="md:col-span-5">
          <p className="overline opacity-70">{cfg.newsletterOverline}</p>
          <h3 className="font-serif-display text-3xl md:text-5xl mt-3 leading-tight">
            {cfg.newsletterHeadline}
          </h3>
          <p className="mt-4 text-sm opacity-70 max-w-md leading-relaxed">
            {cfg.newsletterCopy}
          </p>
          <form
            onSubmit={subscribe}
            data-testid="footer-newsletter-form"
            className="mt-6 flex border-b border-[color:var(--vw-bg)]/40 focus-within:border-[color:var(--vw-bg)] transition-colors"
          >
            <input
              type="email"
              required
              data-testid="newsletter-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="flex-1 bg-transparent py-3 outline-none placeholder:text-[color:var(--vw-bg)]/50 text-sm font-utility"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit"
              className="px-3 py-3 disabled:opacity-40 hover:opacity-70 transition-opacity"
              aria-label="Subscribe"
            >
              <Send size={18} strokeWidth={1.5} />
            </button>
          </form>
        </div>

        {/* Shop */}
        <div className="md:col-span-2 md:col-start-7">
          <p className="overline opacity-70 mb-5">Shop</p>
          <ul className="space-y-3 text-sm">
            {shopLinks.map((l) => (
              <li key={l.to}><Link to={l.to} className="hover:opacity-70">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        {/* House */}
        <div className="md:col-span-2">
          <p className="overline opacity-70 mb-5">House</p>
          <ul className="space-y-3 text-sm">
            <li><Link to="/about" className="hover:opacity-70">About</Link></li>
            <li><Link to="/contact" className="hover:opacity-70">Contact</Link></li>
            <li><Link to="/faq" className="hover:opacity-70">FAQ</Link></li>
          </ul>
        </div>

        {/* Policies */}
        <div className="md:col-span-3">
          <p className="overline opacity-70 mb-5">Policies</p>
          <ul className="space-y-3 text-sm">
            <li><Link to="/shipping" className="hover:opacity-70">Shipping</Link></li>
            <li><Link to="/returns" className="hover:opacity-70">Returns</Link></li>
            <li><Link to="/privacy" className="hover:opacity-70">Privacy</Link></li>
            <li><Link to="/terms" className="hover:opacity-70">Terms</Link></li>
          </ul>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-10">
        <p className="text-xs opacity-60 font-utility">
          © {new Date().getFullYear()} Vastra West. Where elegance meets everyday.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
